import {
  ParsedParamsFromUrl,
  RawNonFilterParams,
  RawSearchFilters,
  SearchFilterKeys,
  SearchFiltersState,
  SearchFiltersStateFilters,
  SearchFiltersStateFiltersKeys,
  defaultFilters,
} from '@cinch-nx/data-search'
import { createStore } from 'zustand'
import { devtools } from 'zustand/middleware'
import { convertRawSearchFiltersToSearchFilters } from '../../utils'
import { urlMiddleware } from './url-middleware'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { scrollToTarget } from '../../utils/scroll-to-target/scroll-to-target'

type PartialState = {
  [key in SearchFiltersStateFiltersKeys]?: SearchFiltersState[key]
}

const facetDependants = new Map<SearchFilterKeys, SearchFilterKeys[]>([
  ['make', ['model', 'trim']],
  ['model', ['trim']],
])

const convertPartialRawFiltersToPartialFilters = (
  pathSegmentsFromUrl: Partial<RawSearchFilters>,
) =>
  Object.fromEntries(
    Object.entries(pathSegmentsFromUrl).map(([key, value]) => [
      key,
      value.split(','),
    ]),
  )

export const initialiseSearchStore = ({
  params,
  isInsideStoryblok,
  isSsrUserAgentCinchApp,
  allCinchStores,
  pathSegmentsFromUrl,
  shouldUseDataDrivenSortOrderAsDefault,
}: {
  params: RawSearchFilters & RawNonFilterParams
  isInsideStoryblok: boolean
  isSsrUserAgentCinchApp?: boolean
  allCinchStores: string[]
  pathSegmentsFromUrl: Partial<RawSearchFilters>
  shouldUseDataDrivenSortOrderAsDefault: boolean
}) => {
  const allCinchStoresLowercase = allCinchStores.map((value) =>
    value.toLowerCase(),
  )
  const shouldShowFinanceTypeFilter =
    readFromEnv(Env.SearchFinanceTypeFilter) === 'true'

  const initialState = convertRawSearchFiltersToSearchFilters(
    params,
    allCinchStoresLowercase,
    shouldShowFinanceTypeFilter,
    shouldUseDataDrivenSortOrderAsDefault,
  )

  const pathSegmentFilters =
    convertPartialRawFiltersToPartialFilters(pathSegmentsFromUrl)

  return createStore<SearchFiltersState, [['zustand/devtools', never]]>(
    devtools(
      urlMiddleware(
        (set, get) => ({
          ...initialState,
          isInsideStoryblok,
          isSsrUserAgentCinchApp,
          allCinchStores,
          allCinchStoresLowercase,
          pathSegmentFilters,
          updateFilter(filter, value) {
            const newState: PartialState = {
              pageNumber: 1,
            }

            // this clears a facets dependants. For example if a make is changed,
            // the facets 'model' and 'trim' will be cleared also as they depend on make.
            // Dependants are maintained in the `facetDependants` map
            if (facetDependants.has(filter)) {
              const dependants = facetDependants.get(filter)
              dependants?.forEach((dependant) => (newState[dependant] = []))
            }

            if (filter === 'make' || filter === 'model') {
              newState[filter] = value[0] === '' ? [] : value
            } else if (filter === 'store' && value[0] === 'all-stores') {
              newState.store = [...get().allCinchStores]
            } else {
              newState[filter] = value[0] === '-1' ? [] : value
            }

            set(
              (state) => ({
                ...state,
                ...newState,
              }),
              undefined,
              { type: 'search:updateFilter', filter, value },
            )

            scrollToTarget()
          },
          /**
           * This merges the provided partial filter state over the existing state
           *
           * Note that this does *not* apply any of the logic that `updateFilter` would,
           * so you can end up with a state that has invalid combinations of make/model/trim,
           * or invalid values like `-1` or `` instead of clearing the filter value
           */
          updateFilters(filters: Partial<SearchFiltersStateFilters>) {
            set(
              (state) => ({
                ...state,
                ...filters,
              }),
              undefined,
              { type: 'search:updateFilters', filters },
            )

            scrollToTarget()
          },
          /**
           * Update the search params state using the ParsedParamsFromUrl, merged over the default params
           *
           * This will reset the existing search params state, only setting values present in parsedParamsFromUrl
           */
          updateSearchParamsStateFromResponse(
            parsedParamsFromUrl: ParsedParamsFromUrl,
            pathSegmentsFromUrl: Partial<RawSearchFilters> = {},
          ) {
            const newSearchParamsState = convertRawSearchFiltersToSearchFilters(
              parsedParamsFromUrl,
              get().allCinchStoresLowercase,
              shouldShowFinanceTypeFilter,
              shouldUseDataDrivenSortOrderAsDefault,
            )
            const newPathSegmentFiltersState =
              convertPartialRawFiltersToPartialFilters(pathSegmentsFromUrl)

            set(
              (state) => ({
                ...state,
                ...newSearchParamsState,
                pathSegmentFilters: newPathSegmentFiltersState,
              }),
              undefined,
              {
                type: 'search:updateSearchParamsStateFromResponse',
                parsedParamsFromUrl,
                pathSegmentsFromUrl,
              },
            )
          },
          clearFilterValue(filterType, filterValue) {
            set(
              (state) => {
                const newState: PartialState = {
                  pageNumber: 1,
                  [filterType]: state[filterType].filter(
                    (value) => value !== filterValue,
                  ),
                }

                // this clears a facets dependants. For example if a make is cleared,
                // the facets 'model' and 'trim' will be cleared also as they depend on make.
                // Dependants are maintained in the `facetDependants` map
                if (facetDependants.has(filterType)) {
                  const dependants = facetDependants.get(filterType)
                  dependants?.forEach((dependant) => (newState[dependant] = []))
                }

                return {
                  ...state,
                  ...newState,
                  // closing a finance type filter pill should default to financeType=any to keep the user in the monthly price journey
                  ...(filterType === 'financeType'
                    ? { financeType: ['any'] }
                    : {}),
                }
              },
              undefined,
              { type: 'search:clearFilterValue', filterType, filterValue },
            )

            scrollToTarget()
          },
          clearAllFilters() {
            set(
              (state) => ({
                ...state,
                ...defaultFilters,
                pageNumber: 1,
              }),
              undefined,
              { type: 'search:clearAllFilters' },
            )

            scrollToTarget()
          },
          setPageNumber(page) {
            set((state) => ({ ...state, pageNumber: page }), undefined, {
              type: 'search:setPageNumber',
              page,
            })
          },
          setPageSize(size) {
            set(
              (state) => ({ ...state, pageSize: size, pageNumber: 1 }),
              undefined,
              { type: 'search:setPageSize', size },
            )
          },
          setSortingCriteria(sortingCriteria) {
            set(
              (state) => ({ ...state, sortingCriteria, pageNumber: 1 }),
              undefined,
              { type: 'search:setSortingCriteria', sortingCriteria },
            )

            scrollToTarget()
          },
          setUseMonthly(useMonthly) {
            set(
              (state) => ({
                ...state,
                useMonthly,
                toPrice: [],
                fromPrice: [],
                pageNumber: 1,
              }),
              undefined,
              { type: 'search:setUseMonthly', useMonthly },
            )

            scrollToTarget()
          },
          isCinchStoreDrawerOpen: false,
          setIsCinchStoreDrawerOpen(open) {
            set(
              (state) => ({ ...state, isCinchStoreDrawerOpen: open }),
              undefined,
              { type: 'search:setIsCinchStoreDrawerOpen', open },
            )
          },
        }),
        shouldUseDataDrivenSortOrderAsDefault,
      ),
      {
        enabled: process.env['NODE_ENV'] === 'development',
        name: 'cinch/search',
      },
    ),
  )
}

export type SearchStoreType = ReturnType<typeof initialiseSearchStore>
